// ORKSTRA Theme — Provider
// Source of truth for the active color mode at runtime. Wraps the app once
// (in main.tsx) and exposes the current preference / resolved mode via
// useTheme().

import { useCallback, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import type { ColorMode, ThemeContextValue, ThemePreference } from './types'
import { ThemeContext } from './theme-context'
import {
  loadStoredPreference,
  saveStoredPreference,
} from './theme-storage'
import {
  applyThemeToDocument,
  getSystemMode,
  resolveMode,
  subscribeToSystemMode,
} from './theme-utils'

export interface ThemeProviderProps {
  children: ReactNode
  /** Override the initial preference (mainly for tests). */
  initialPreference?: ThemePreference
  /** Skip touching <html> (mainly for tests / SSR). */
  disableDomEffects?: boolean
}

export function ThemeProvider({
  children,
  initialPreference,
  disableDomEffects = false,
}: ThemeProviderProps) {
  const [preference, setPreferenceState] = useState<ThemePreference>(
    () => initialPreference ?? loadStoredPreference(),
  )
  const [systemMode, setSystemMode] = useState<ColorMode>(() => getSystemMode())

  const mode = resolveMode(preference, systemMode)

  // Apply theme to <html> + sync meta theme-color whenever the resolved mode
  // changes.
  useEffect(() => {
    if (disableDomEffects) return
    applyThemeToDocument(mode)
  }, [mode, disableDomEffects])

  // Track system preference so 'auto' updates live when the OS flips.
  useEffect(() => {
    return subscribeToSystemMode((next) => {
      setSystemMode(next)
    })
  }, [])

  const setPreference = useCallback((next: ThemePreference) => {
    setPreferenceState(next)
    saveStoredPreference(next)
  }, [])

  const value = useMemo<ThemeContextValue>(
    () => ({
      preference,
      mode,
      systemMode,
      isAuto: preference === 'auto',
      setPreference,
    }),
    [preference, mode, systemMode, setPreference],
  )

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}
